import { sanitizeCardDescription } from '@/utils/sanitize'
import { capitalizeFirstLetter } from '@/helpers/formatting'
import { functionType } from '@/composables/useWorkflowAssistantFunctions'

const findCardById = (svgGraph, cardId) => {
  for (const eventId of Object.keys(svgGraph)) {
    const cards = svgGraph[eventId]?.cards || []
    const card = cards.find((c) => c.id === cardId)
    if (card) {
      return card
    }
  }

  return null
}

export const getUpdateSummary = (type, args, { workflow, svgGraph, svgLanes, cardTypes }, result) => {
  const event = args.eventId ? svgGraph[args.eventId] : null
  const lane = args.laneId ? svgLanes[args.laneId] : null
  const cardType = args.cardTypeId ? cardTypes.find((item) => item.id === args.cardTypeId) : null
  const entity = args.entityId ? (workflow.schemas ?? []).find((item) => item.id === args.entityId) : null
  const card = args.cardId ? findCardById(svgGraph, args.cardId) : null

  switch (type) {
    case functionType.UPDATE_WORKFLOW_NAME:
      return `Changed workflow name to "${args.name}"`
    case functionType.CREATE_EVENT:
      return `Created new event "${args.description}"`
    case functionType.UPDATE_EVENT_DESCRIPTION:
      return `Updated event description to "${args.description}"`
    case functionType.DELETE_EVENT:
      return `Deleted event "${event?.name ?? ''}"`
    case functionType.CREATE_LANE:
      return `Created new lane "${args.name}"`
    case functionType.UPDATE_LANE_NAME:
      return `Updated lane name to "${args.name}"`
    case functionType.DELETE_LANE:
      return `Deleted lane "${lane?.name ?? ''}"`
    case functionType.MOVE_LANE:
      return `Rearranged lane "${lane?.name ?? ''}"`
    case functionType.CREATE_CARD:
      return `Created new ${cardType?.title ?? ''} card on "${event?.name ?? ''}"`
    case functionType.DELETE_CARD:
      return `Deleted card "${sanitizeCardDescription(card?.description ?? '')}"`
    case functionType.UPDATE_CARD: {
      const updates = []
      if (args.description !== undefined) {
        updates.push(`description to "${args.description}"`)
      }
      if (args.schemaId !== undefined) {
        updates.push(args.schemaId ? 'linked schema' : 'unlinked schema')
      }

      return `Updated card: ${updates.join(', ') || 'no changes'}`
    }
    case functionType.VOTE_CARD:
      return `${capitalizeFirstLetter(args.voteType)}d card.`
    case functionType.CREATE_ENTITY:
      return `Created new entity "${args.name}"`
    case functionType.UPDATE_ENTITY:
      return `Updated entity "${args.name}"`
    case functionType.DELETE_ENTITY:
      return `Deleted entity "${entity?.name ?? ''}"`
    case functionType.CREATE_COMMAND:
      return `Created command "${args.name}" on event "${event?.name ?? ''}"`
    case functionType.UPDATE_COMMAND:
      return `Updated command on event "${event?.name ?? ''}"`
    case functionType.CREATE_READ_MODEL:
      return `Created read model fields on event "${event?.name ?? ''}"`
    case functionType.UPDATE_READ_MODEL:
      return `Updated read model on event "${event?.name ?? ''}"`
    case functionType.REGENERATE_CARDS:
      if (result?.error) {
        return result.error
      }

      if (result?.created === 0) {
        return 'No cards were regenerated'
      }

      return `Regenerated ${result?.created ?? 0} cards (deleted ${result?.deleted ?? 0} old cards)`
    case functionType.ADD_MISSING_CARDS:
      if (result?.error) {
        return result.error
      }

      if (result?.created === 0) {
        return 'No missing cards found - all events already have cards for enabled card types'
      }

      return `Added ${result?.created ?? 0} missing cards`
    case functionType.REGENERATE_COMMANDS:
      if (result?.created === 0) {
        return 'No commands were regenerated'
      }

      return `Regenerated commands on ${result?.created ?? 0} events (deleted ${result?.deleted ?? 0} old)`
    case functionType.ADD_MISSING_COMMANDS:
      if (result?.created === 0) {
        return 'No missing commands found - all Command cards already have fields defined'
      }

      return `Added commands to ${result.created} events`
    case functionType.REGENERATE_READ_MODELS:
      if (result?.created === 0) {
        return 'No read models were regenerated'
      }

      return `Regenerated read models on ${result?.created ?? 0} events (deleted ${result?.deleted ?? 0} old)`
    case functionType.ADD_MISSING_READ_MODELS:
      if (result?.created === 0) {
        return 'No missing read models found - all ReadModel cards already have fields defined'
      }

      return `Added read models to ${result.created} events`
    case functionType.REGENERATE_ENTITIES:
      if (result?.error) {
        return result.error
      }

      if (result?.created === 0) {
        return 'No entities were regenerated'
      }

      return `Regenerated ${result?.created ?? 0} entities (deleted ${result?.deleted ?? 0} old entities)`
    case functionType.ADD_MISSING_ENTITIES:
      if (result?.error) {
        return result.error
      }

      if (result?.created === 0) {
        return result?.skipped > 0
          ? `All ${result.skipped} suggested entities already exist`
          : 'No missing entities found'
      }

      return result?.skipped > 0
        ? `Added ${result.created} entities (${result.skipped} already existed)`
        : `Added ${result.created} entities`
    case functionType.GENERATE_ENTITIES_FROM_DESCRIPTION:
      if (result?.deleted !== undefined) {
        return `Generated ${result.created} entities from description (deleted ${result.deleted} old entities)`
      }

      if (result?.created === 0) {
        return result?.skipped > 0
          ? `All ${result.skipped} entities from description already exist`
          : 'No entities were generated from description'
      }

      return result?.skipped > 0
        ? `Generated ${result.created} entities from description (${result.skipped} already existed)`
        : `Generated ${result.created} entities from description`
    case functionType.GENERATE_COMMANDS_FROM_DESCRIPTION:
      if (result?.deleted !== undefined) {
        return `Generated commands on ${result.created} events from description (deleted ${result.deleted} old)`
      }

      if (result?.created === 0) {
        return 'No commands were generated from description'
      }

      return `Generated commands on ${result.created} events from description`
    case functionType.GENERATE_READ_MODELS_FROM_DESCRIPTION:
      if (result?.deleted !== undefined) {
        return `Generated read models on ${result.created} events from description (deleted ${result.deleted} old)`
      }

      if (result?.created === 0) {
        return 'No read models were generated from description'
      }

      return `Generated read models on ${result.created} events from description`
    case functionType.GENERATE_CARDS_FROM_DESCRIPTION:
      if (result?.error) {
        return result.error
      }

      if (result?.deleted !== undefined) {
        return `Generated ${result.created} cards from description (replaced ${result.deleted} existing cards)`
      }

      if (result?.created === 0) {
        return 'No cards were generated - all events already have cards for enabled card types'
      }

      return `Generated ${result.created} cards from description`
    case functionType.PLAN_WORKFLOW_WITH_AI:
      if (result?.error) {
        return result.error
      }

      if (!result?.events?.length) {
        return 'No events were planned from the description'
      }

      const eventsList = result.events
        .map((e, i) => `${i + 1}. ${e.role}: "${e.domainEvent}"`)
        .join('\n')

      return `Planned ${result.events.length} events:\n${eventsList}`
    case functionType.VALIDATE_DOMAIN_MODEL:
      if (result?.isValid) {
        return 'Domain model validation passed - no issues found.'
      }

      const validationIssues = result?.issues || []
      const issueLines = validationIssues.map((issue) => `- ${issue.message}`)

      return `Found ${validationIssues.length} issue${validationIssues.length === 1 ? '' : 's'}:\n${issueLines.join('\n')}`
    case functionType.ANALYZE_DOMAIN_MODEL:
      if (result?.error) {
        return result.error
      }

      return result?.summary || 'Domain model analysis completed.'
    case functionType.ROLLBACK_WORKFLOW:
      if (result?.error) {
        return result.error
      }

      return result?.message || 'Workflow rollback completed.'
    default:
      return ''
  }
}

export const getUpdatesSummaryText = (updates = []) => {
  if (updates.length === 0) {
    return 'What else can I help you with?'
  }

  const multiple = updates.length > 1
  const header = multiple
    ? 'The following changes were applied to the workflow:\n'
    : ''

  const body = updates
    .map((item) => multiple ? `- ${item}` : item)
    .join(multiple ? '\n' : '.\n') +
    (multiple ? '\n' : '.\n')

  const footer = '\nWhat else can I help you with?'

  return header + body + footer
}
